import React, { useMemo, useState } from 'react'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { BreadcrumbView } from '../../context/breadcrumb.context'
import { Form, FormSubmitBack, FormTextConfirm } from '../form/form'
import { useParamsInt } from '../hooks/location'
import { Card } from '../card/card'
import { IconDelete } from '../icon/icon'
import { useNotificationContext } from '../../context/notification/context'
import { isLastOwner, recycleErrorMessage, refusedOrganizations, serverMessage } from '../error/server-message'
import { RefusalLinks } from '../error/last-owner-links'
import { ErrorReference } from '../error/error-reference'
import { withReference } from '../error/error-notice'

// `showServerError`: show why a purge was refused (e.g. 409 account.last_owner).
// Off by default, so existing purge pages behave exactly as before.
// `lastOwnerLinks(organizations, form)`: on a last-owner refusal, the links
// shown after the message ([{to, text}]; see shared/error/last-owner-links).
// `buttonText` / `successMessage`: the submit button's word and the toast on
// success (default "purge" / "purge success"); e.g. the organization recycle
// bin's "purge now", which only schedules the purge (202).
export const Purge = ({ useApiForm, titleParam, successPath, onSuccess, showServerError, lastOwnerLinks, buttonText, successMessage }) => {
  const apiForm = useApiForm()
  const params = useParams()
  const history = useHistory()

  const notificationContext = useNotificationContext()

  const { purgeID } = useParamsInt()

  const [error, setError] = useState(null)
  const [failure, setFailure] = useState(null)
  const [links, setLinks] = useState(null)

  const title = useMemo(() => `purge ${apiForm.form[titleParam] || ''}`, [apiForm.form, titleParam])
  // The crumb names the record, as the heading does, so the tab does too
  // (audit F22).
  const crumb = apiForm.form[titleParam] ? `Purge ${apiForm.form[titleParam]}` : 'Purge'

  const handleMatch = () => {
    apiForm.get(`${purgeID}/deleted`)
  }

  // Leaves this confirm page the same way a successful purge does:
  // the caller's onSuccess (e.g. history.goBack()), or push to successPath.
  const leave = () => {
    if (onSuccess) {
      onSuccess(apiForm.form)
    } else {
      let path = generatePath(successPath, params)
      history.push(path)
    }
  }

  const handleSubmit = async () => {
    setError(null)
    setFailure(null)
    setLinks(null)
    try {
      await apiForm.delete(`${purgeID}/purge`)
      notificationContext.toast.notify(successMessage || 'purge success', 3000)
      leave()
    } catch (err) {
      if (showServerError) {
        // Unchanged: an uncoded body (old servers, raw database errors) still
        // gets the page's own fallback, never the raw text, same as before.
        setError(serverMessage(err, "This couldn't be purged."))
        setFailure(err)
        if (lastOwnerLinks && isLastOwner(err)) setLinks(lastOwnerLinks(refusedOrganizations(err), apiForm.form))
      } else {
        // This caller didn't opt into the inline alert (showServerError), so
        // there is nowhere to show it and stay -- say what happened via a
        // toast (it outlives the navigation) and leave the page the same
        // way a success does. Nothing is retryable here either way: a 404
        // means the row is already gone, and any other failure still leaves
        // this confirm page with no useApiForm state worth resubmitting.
        notificationContext.toast.notify(withReference(recycleErrorMessage(err, 'This was already purged.'), err), 5000)
        leave()
      }
    }
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconDelete} text={crumb} onMatch={handleMatch}>
        <Card title={title}>
          <div className="py-2">This is a destructive action</div>
          {error &&
            <p className="text-danger" role="alert">
              {error}
              <RefusalLinks links={links} />
            </p>}
          {error && <ErrorReference error={failure} className="mb-3" />}
          <Form state={apiForm.form} onSubmit={handleSubmit}>
            <FormTextConfirm placeholder="purge" subtext="Type purge to confirm" confirmValue="purge" name="test" required autoFocus />
            <FormSubmitBack text={buttonText || 'purge'} />
          </Form>
        </Card>
      </BreadcrumbView>
    </React.Fragment>
  )
}
